import React, {useState, useMemo} from 'react'
import {ErrorBoundary} from 'react-error-boundary'
import {useTranslation} from 'react-i18next'
import {useHistory} from 'react-router-dom'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {Box, Grid} from '@mui/material'
import {
  getProductLineColors,
  getSpacing,
  PGAvatar,
  PGButton,
  PGCard,
  PGModal,
  PGSelectField,
  PGTheme,
  PGTypography,
} from '@progynyglobal/pg-ui-library'
import {useSnackbar} from 'notistack'
import {makeStyles} from 'tss-react/mui'

import {ReactComponent as QuestionMarkIcon} from '@progyny/assets/images/question-mark.svg'
import {ErrorBoundaryFallback} from '@progyny/components/ErrorBoundary'
import {
  useUpdateMemberJourneyMutation,
  MeDocument,
} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import {SubpageHeader} from '@progyny/pages/Profile/fragments/header'
import JourneyComparisonTable from '@progyny/pages/SwitchJourney/JourneyComparisonTable'
import JourneyInfoCard from '@progyny/pages/SwitchJourney/JourneyInfoCard'
import ProductGuide from '@progyny/pages/SwitchJourney/ProductGuide'
import {useRoutes, RouteEnum} from '@progyny/routes'
import {useMeStore} from '@progyny/store/meStore'
import {useRefetchContext} from '@progyny/store/refetchStore'
import {
  FERTILITY,
  MENOPAUSE,
  PREGNANCY_AND_POSTPARTUM,
} from '@progyny/utils/constants'

// Map product line values to display labels
const PRODUCT_LINE_LABELS: Record<string, string> = {
  fertility: 'Fertility',
  pregnancy_and_postpartum: 'Pregnancy & Postpartum',
  menopause: 'Menopause',
}

// Map display values to product line values (for select field)
const DISPLAY_TO_PRODUCT_LINE: Record<string, string> = {
  fertility: FERTILITY,
  pregnancy: PREGNANCY_AND_POSTPARTUM,
  menopause: MENOPAUSE,
}

// Map product line values to display values (for JourneyComparisonTable)
const PRODUCT_LINE_TO_DISPLAY: Record<
  string,
  'fertility' | 'pregnancy' | 'menopause'
> = {
  fertility: FERTILITY,
  pregnancy_and_postpartum: 'pregnancy',
  menopause: MENOPAUSE,
}

const SwitchJourney = () => {
  const {t} = useTranslation()
  const user = useMeStore((s) => s.user)
  const evaluateProfileForProductLine = useMeStore(
    (s) => s.evaluateProfileForProductLine
  )
  const {productLine} = useProductLine()
  const colors = getProductLineColors(productLine)
  const {classes} = useStyles({colors})
  const {enqueueSnackbar} = useSnackbar()
  const {triggerRefetch} = useRefetchContext()
  const {getPath} = useRoutes()
  const history = useHistory()
  const [updateMemberJourneyMutation] = useUpdateMemberJourneyMutation()
  const [showChangeJourneyModal, setChangeJourneyModal] = useState(false)
  const [selectedJourney, setJourney] = useState<string | number>('')

  // Get available product lines and active journey from me query
  const availableProductLines = useMemo(
    () => user?.member?.availableProductLines || [],
    [user?.member?.availableProductLines]
  )
  const activeMemberJourney = user?.member?.activeMemberJourney

  // Create options from availableProductLines, excluding activeMemberJourney
  const journeyOptions = useMemo(() => {
    return availableProductLines
      .filter(
        (line): line is string =>
          line !== null &&
          line !== undefined &&
          line !== activeMemberJourney &&
          PRODUCT_LINE_LABELS[line] !== undefined
      )
      .map((line) => {
        const displayValue = PRODUCT_LINE_TO_DISPLAY[line] || line
        return {
          value: displayValue,
          label: t(PRODUCT_LINE_LABELS[line]) || line,
        }
      })
  }, [availableProductLines, activeMemberJourney, t])

  // Get the selected journey's product line value for mutation
  const getSelectedProductLineValue = (
    displayValue: string | number
  ): string => {
    if (typeof displayValue === 'string') {
      // Map display value back to product line value
      return DISPLAY_TO_PRODUCT_LINE[displayValue] || displayValue
    }
    return String(displayValue)
  }

  const handleSwitchJourney = () => {
    setChangeJourneyModal(true)
  }

  const handleAddJourney = async () => {
    if (!selectedJourney) return

    try {
      const productLineValue = getSelectedProductLineValue(selectedJourney)
      await updateMemberJourneyMutation({
        variables: {
          memberJourney: productLineValue,
          endCurrentJourney: false,
        },
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      })

      // Only refetch article categories (not individual article queries)
      triggerRefetch('fetchArticleCategories')

      // Check if the selected journey requires onboarding and if it's incomplete
      // Use the centralized evaluateProfileForProductLine function
      const isOnboardingComplete =
        evaluateProfileForProductLine(productLineValue)

      // If onboarding is incomplete for any journey (fertility, menopause, or pregnancy/postpartum),
      // redirect to setup profile
      if (!isOnboardingComplete) {
        setChangeJourneyModal(false)
        setJourney('')
        enqueueSnackbar(t('switch_journey_journey_added_successfully'), {
          variant: 'success',
        })
        history.push(getPath(RouteEnum.SETUP_PROFILE))
        return // Early return to prevent further execution
      }

      // Onboarding is complete, redirect to home
      setChangeJourneyModal(false)
      setJourney('')
      enqueueSnackbar(t('switch_journey_journey_added_successfully'), {
        variant: 'success',
      })
      history.push(getPath(RouteEnum.HOME))
    } catch (error) {
      enqueueSnackbar(t('switch_journey_failed_to_add_journey'), {
        variant: 'error',
      })
    }
  }

  const handleEndJourney = async () => {
    if (!selectedJourney) return

    try {
      const productLineValue = getSelectedProductLineValue(selectedJourney)
      await updateMemberJourneyMutation({
        variables: {
          memberJourney: productLineValue,
          endCurrentJourney: true,
        },
        refetchQueries: [{query: MeDocument}],
        awaitRefetchQueries: true,
      })

      // Only refetch article categories (not individual article queries)
      triggerRefetch('fetchArticleCategories')

      // When ending a journey, we switch to an existing journey that was already set up
      // So we should always redirect to home (no onboarding check needed)
      // The onboarding check is only needed when ADDING a new journey
      setChangeJourneyModal(false)
      setJourney('')
      enqueueSnackbar(t('switch_journey_journey_updated_successfully'), {
        variant: 'success',
      })
      history.push(getPath(RouteEnum.HOME))
    } catch (error) {
      enqueueSnackbar(t('switch_journey_failed_to_update_journey'), {
        variant: 'error',
      })
    }
  }

  return (
    <Box
      mt={{xs: '80px', md: '100px'}}
      paddingX={{xs: 2, md: '24px'}}
      paddingBottom={'100px'}
    >
      <ErrorBoundary fallbackRender={ErrorBoundaryFallback}>
        <SubpageHeader
          title={t('switch_journey_explore_my_journey_options')}
          subTitle={t('switch_journey_we_re_here_for_every')}
        />
        <Grid container spacing={7} mt={0}>
          <Grid item xs={12} md={6} lg={6}>
            <PGCard
              className={classes.productGuideCard}
              header={t('switch_journey_step_by_step_guide')}
              children={<ProductGuide />}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={6}>
            <PGCard
              header={t('switch_journey_select_journey')}
              className={classes.productLineCard}
              children={
                <Box>
                  <PGSelectField
                    options={journeyOptions}
                    variant='standard'
                    value={selectedJourney}
                    displayEmpty
                    renderValue={(selected) => {
                      const stringValue = String(selected ?? '')
                      if (!stringValue) {
                        return (
                          <span className={classes.placeholder}>
                            {t('switch_journey_select_your_next_journey')}
                          </span>
                        )
                      }
                      const option = journeyOptions.find(
                        (opt) => String(opt.value) === stringValue
                      )
                      return option?.label ?? stringValue
                    }}
                    disabled={
                      user?.member?.availableProductLines?.length === 1 &&
                      user?.member?.availableProductLines[0] ===
                        user?.member?.activeMemberJourney
                    }
                    onChange={(val) => setJourney(val)}
                    className={classes.selectField}
                    data-testid='switch-journey-select'
                  />
                  <PGButton
                    className={classes.saveBtn}
                    label={t('switch_journey_save_and_continue')}
                    endIcon={<ArrowForwardIcon />}
                    onClick={handleSwitchJourney}
                    disabled={!selectedJourney}
                    data-testid='switch-journey-save'
                  />
                </Box>
              }
            />
            <JourneyInfoCard classes={classes} />
          </Grid>
        </Grid>
        {showChangeJourneyModal && (
          <PGModal
            disableBackdropClick
            className={classes.journeyModal}
            open={showChangeJourneyModal}
            avatarCloseButton
            onClose={() => setChangeJourneyModal(false)}
            title={
              <PGTypography variant='title' className={classes.modalTitle}>
                <PGAvatar className={classes.questionMark}>
                  <QuestionMarkIcon />
                </PGAvatar>
                {t('switch_journey_do_you_want_to_add')}
              </PGTypography>
            }
          >
            <JourneyComparisonTable
              onAddJourney={handleAddJourney}
              onEndJourney={handleEndJourney}
            />
          </PGModal>
        )}
      </ErrorBoundary>
    </Box>
  )
}

export default SwitchJourney

const useStyles = makeStyles<{colors?: any}>()((theme, {colors}) => ({
  saveBtn: {
    marginTop: 64,
  },
  productLineCard: {
    backgroundColor: colors.primaryLight,
  },
  selectField: {
    backgroundColor: PGTheme.common.white,
    borderRadius: 8,
  },
  placeholder: {
    color: theme.palette.text.secondary,
  },
  productGuideCard: {},
  journeyInfo: {
    marginTop: 54,
  },
  avatarInfo: {
    border: `1px solid ${PGTheme.common.blueLight}`,
  },
  journeyModal: {
    '& .MuiPaper-root': {
      minWidth: 500,
      maxWidth: 850,
      '& .MuiTypography-root': {
        paddingTop: getSpacing(32),
        '& .MuiBox-root': {
          top: getSpacing(20),
          right: getSpacing(32),
        },
      },
    },
  },
  modalTitle: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
  questionMark: {
    width: 74,
    height: 74,
    border: 'none',
    backgroundColor: PGTheme.common.peach,
  },
  journeyBtn: {
    alignSelf: 'center',
    marginTop: 'auto',
  },
}))
